import axios from "axios";
import { encodeSpecialChars } from '../middleware/encodingMiddleware';
import { setIsSessionExpired } from "../redux/slice/loginSlice";
import { API_URL } from "../utils/config";
import { store } from './store';

const instance = axios.create({
  baseURL: API_URL,
  headers: {},
  withCredentials: true
});

const pendingRequests = new Map();

// Cancel all active/pending API requests
const cancelAllPendingRequests = () => {
  for (const [, controller] of pendingRequests) {
    controller.abort();
  }
  pendingRequests.clear();
};

instance.interceptors.request.use(
  async (config) => {
    const isLogoutRequest = config.url?.includes("/logout");

    if (!isLogoutRequest) {
      // Assign AbortController to each request
      const controller = new AbortController();
      config.signal = controller.signal;

      const requestKey = `${config.method}-${config.url}`;
      pendingRequests.set(requestKey, controller);
    }
    
    try {
      // Handle request body for POST, PUT, PATCH, excluding FormData/File uploads
      if (config.data &&
        ['post', 'put', 'patch'].includes(config.method?.toLowerCase()) &&
        !(config.data instanceof FormData)) {
        config.data = encodeSpecialChars(config.data);
      }

      // Handle URL params for all requests
      if (config.params) {
        config.params = encodeSpecialChars(config.params);
      } else if (config.url && config.url.includes('?')) {
        // If params are in URL, extract and encode them
        const [baseUrl, queryString] = config.url.split('?');
        const params = {};
        queryString.split('&').forEach(param => {
          const [key, value] = param.split('=');
          params[key] = value;
        });
        config.params = encodeSpecialChars(params);
        config.url = baseUrl;
      }
    } catch (error) {
      console.error('Encoding error:', error);
    }

    config.headers["user-id"] = store.getState().login.userId;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const handleLogoutAndRedirect = (message) => {
  if (!store.getState().login.isLoggedIn) {
    return
  }
  store.dispatch(setIsSessionExpired(true));
  cancelAllPendingRequests();
}

instance.interceptors.response.use(
  (res) => res,
  async (error) => {
    const { response } = error;

    if (response?.status === 401 && response?.data?.message?.toLowerCase().includes("expired")) {
      handleLogoutAndRedirect("Session Expired. Please log in.");
    }

    // Forward all other errors, including 401 not caused by session expiry
    return Promise.reject(response);
  }
);

export default instance;

export { handleLogoutAndRedirect };
